import { c as _c } from "react/compiler-runtime";
import { pinToPositionStyle } from "@osmix/ui-primitives";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ProductPhotoHotspots } from "@/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { coverPinPosition } from "@/lib/hotspot-position";
import { productHref } from "@/lib/product-link";
import { useFormatPrice } from "@/lib/use-format-price";

/**
 * Точки на фото галереи карточки товара (fd-catalog-03). Продавец ставит точку
 * на фото и привязывает другой свой товар; нажатие на точку открывает
 * мини-карточку со ссылкой на этот товар. Вид точки и карточки - как у меток
 * идей в ленте (components/feed/collection-cover.tsx), чтобы покупатель узнавал
 * их везде одинаково. Точку на товар, который сейчас не продаётся, шлюз не отдаёт.
 *
 * Продавец ставит точку на фото целиком, а галерея обрезает фото под свой
 * кадр (`object-fit: cover`): координаты пересчитываются по пропорциям фото
 * и кадра, точка в срезанной части не рисуется (lib/hotspot-position.ts).
 */
export function PhotoHotspots(t0) {
  const $ = _c(18);
  const {
    hotspots,
    frameRatio
  } = t0;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const src = hotspots?.photo;
  const [imageRatio, setImageRatio] = useState(null);
  let t1;
  let t2;
  if ($[0] !== src) {
    t1 = () => {
      if (!src) {
        return;
      }
      let alive = true;
      const img = new Image();
      img.onload = () => {
        if (alive && img.naturalWidth && img.naturalHeight) {
          setImageRatio(img.naturalWidth / img.naturalHeight);
        }
      };
      img.onerror = () => alive && setImageRatio(0);
      img.src = src;
      return () => {
        alive = false;
      };
    };
    t2 = [src];
    $[0] = src;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (imageRatio == null) {
    return null;
  }
  let t3;
  let t4;
  if ($[3] !== fmt || $[4] !== frameRatio || $[5] !== hotspots?.pins || $[6] !== imageRatio || $[7] !== t) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t5;
      if ($[10] !== frameRatio || $[11] !== imageRatio) {
        t5 = pin => {
          const at = pin.product ? coverPinPosition(pin, imageRatio, frameRatio) : null;
          return at ? [{
            ...pin,
            ...at
          }] : [];
        };
        $[10] = frameRatio;
        $[11] = imageRatio;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      const pins = (hotspots?.pins ?? []).flatMap(t5);
      if (pins.length === 0) {
        t4 = null;
        break bb0;
      }
      let t6;
      if ($[13] !== fmt || $[14] !== t) {
        t6 = pin_0 => {
          const product = pin_0.product;
          if (!product) {
            return null;
          }
          return <Popover key={pin_0.id}><PopoverTrigger aria-label={t("product.hotspotLabel", {
              name: product.name
            })} data-testid="photo-hotspot" className="group/pin absolute z-10 flex size-9 items-center justify-center rounded-full outline-none" style={pinToPositionStyle(pin_0)}><span className="size-5 rounded-full border-2 border-white bg-osmix-tangelo shadow-md ring-1 ring-black/10 transition-transform duration-150 group-hover/pin:scale-110 group-focus-visible/pin:scale-110 group-data-[state=open]/pin:scale-110" /></PopoverTrigger><PopoverContent side="top" align="center" sideOffset={10} collisionPadding={16} className="w-56 rounded-lg border-osmix-gray-300 p-0 shadow-top-deep"><Link to={productHref(product.id)} data-testid="photo-hotspot-card" className="flex items-center gap-3 rounded-lg p-2 outline-none transition-colors hover:bg-osmix-gray-100 focus-visible:bg-osmix-gray-100">{product.photo ? <img src={product.photo} alt="" className="size-14 shrink-0 rounded-md bg-osmix-gray-100 object-cover" /> : <span className="size-14 shrink-0 rounded-md bg-osmix-gray-100" />}<span className="flex min-w-0 flex-col gap-0.5">{product.brand && <span className="truncate type-small text-osmix-icon">{product.brand}</span>}<span className="line-clamp-2 type-body2-bold leading-tight text-osmix-text-1">{product.name}</span><span className="flex items-center gap-1 type-body2-bold text-osmix-tangelo">{fmt(product.price)}<ArrowRight className="size-3.5" /></span></span></Link></PopoverContent></Popover>;
        };
        $[13] = fmt;
        $[14] = t;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      t3 = pins.map(t6);
    }
    $[3] = fmt;
    $[4] = frameRatio;
    $[5] = hotspots?.pins;
    $[6] = imageRatio;
    $[7] = t;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[16] !== t3) {
    t5 = <>{t3}</>;
    $[16] = t3;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}